import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import CustomerSelect from './CustomerSelect';
import CustomerChat from './CustomerChat';
import useResizableSidebar from '../hooks/useResizableSidebar';
import ResizeHandle from '../components/ResizeHandle';

// WhatsApp Web Style Split Screen Layout for Customer
// Desktop: Left = Menu, Right = Chat
// Mobile: Full screen navigation (existing behavior)

import axios from 'axios';
import { Wrench } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDashboardLayout = () => {
  const { sidebarWidth, isDragging, startDragging } = useResizableSidebar(420, 260, 650);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeChatType, setActiveChatType] = useState(null);
  const [maintenance, setMaintenance] = useState(null);
  const [isDesktop, setIsDesktop] = useState(() => {
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    return !isPWA && window.innerWidth >= 1024;
  });

  // Check Maintenance Mode
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const res = await axios.get(`${API}/settings/maintenance`);
        setMaintenance(res.data || null);
      } catch (e) {}
    };
    checkMaintenance();
    const interval = setInterval(checkMaintenance, 10000);
    return () => clearInterval(interval);
  }, []);

  // Check for chat type in URL
  useEffect(() => {
    const type = searchParams.get('type');
    if (type) {
      setActiveChatType(type);
    }
  }, [searchParams]);

  // Handle window resize (disabled in PWA mode)
  useEffect(() => {
    const handleResize = () => {
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      setIsDesktop(!isPWA && window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle chat selection from CustomerSelect
  const handleChatSelect = (type) => {
    if (isDesktop) {
      // On desktop, open chat in side panel
      setActiveChatType(type);
      // Update URL without navigation
      window.history.pushState({}, '', `/customer-select?type=${type}`);
    } else {
      // On mobile, navigate to full screen chat
      navigate(`/customer-chat?type=${type}`);
    }
  };

  // Handle back button in chat (close side panel)
  const handleCloseChat = () => {
    setActiveChatType(null);
    window.history.pushState({}, '', '/customer-select');
  };

  // Render Maintenance Mode Screen if enabled
  if (maintenance?.enabled && ['customers', 'both'].includes(maintenance?.who_blocked)) {
    return (
      <div className="fixed inset-0 bg-slate-900 z-[999] flex flex-col items-center justify-center p-6 text-center text-white font-sans animate-fade-in">
        <div className="w-16 h-16 bg-amber-500/20 text-amber-400 rounded-full flex items-center justify-center mb-4 border border-amber-500/30 shadow-lg animate-pulse">
          <Wrench size={32} weight="bold" />
        </div>
        <h2 className="text-xl font-bold mb-2">Maintenance Mode</h2>
        <p className="text-sm text-slate-300 max-w-sm leading-relaxed mb-6">
          {maintenance.message || "We're doing some quick maintenance. Please check back shortly."}
        </p>
        <div className="text-[11px] text-slate-500 font-medium">
          DWS Vault Support System
        </div>
      </div>
    );
  }

  // Mobile: Use existing full-screen navigation
  if (!isDesktop) {
    return <CustomerSelect onChatSelect={handleChatSelect} />;
  }

  // Desktop: Split screen layout
  return (
    <div className="flex h-screen bg-slate-900 overflow-hidden">
      {/* Left Panel - Menu (resizable width) */}
      <div 
        style={activeChatType ? { width: `${sidebarWidth}px` } : undefined}
        className={`${activeChatType ? 'flex-shrink-0' : 'w-full'} h-full transition-all duration-150 ease-in-out overflow-y-auto overflow-x-hidden border-r border-slate-700`}
      >
        <CustomerSelect 
          onChatSelect={handleChatSelect} 
          isCompact={!!activeChatType}
          activeType={activeChatType}
        />
      </div>

      {/* Resize handle between list and chat (Desktop only) */}
      {activeChatType && (
        <ResizeHandle onMouseDown={startDragging} isDragging={isDragging} />
      )}

      {/* Right Panel - Chat (slides in when active) */}
      {activeChatType && (
        <div className="flex-1 h-full bg-gray-100 relative animate-slideIn overflow-hidden">
          <CustomerChat 
            key={activeChatType}
            chatType={activeChatType} 
            isEmbedded={true}
            onBack={handleCloseChat}
          />
        </div>
      )}
    </div>
  );
};

export default CustomerDashboardLayout;
